import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  Button,
  Checkbox,
  ColorsV2,
  FieldHelperText,
  Flex,
  Text,
  TextInput,
  useBreakpoint,
  useThemeColors,
} from '@electricjs/arc';

import { CopyButton, ExternalLink } from '@common';

import {
  ABMManagementCard,
  ResellerIdsList,
  StepContainer,
} from '@/components/ABMManagement/ABMComponents';
import { useABMSetup } from '@/components/ABMManagement/useABMSetup';
import { CA_ABM_RESELLER_ID, US_ABM_RESELLER_ID } from '@/constants/abm';
import { getLocalizedAppleUrl } from '@/helpers/getLocalizedAppleUrl';

const LoginIntoAbmStep = () => {
  const { t } = useTranslation('getting-started');
  return (
    <StepContainer step={1}>
      <Text variant="label-large" mb="1rem">
        {t('abmSetupForm.step1.title')}
      </Text>
      <Text mb="1rem">{t('abmSetupForm.step1.description')}</Text>
      <ExternalLink
        id="abm-go-to-apple-link"
        href={getLocalizedAppleUrl('https://business.apple.com')}>
        {t('abmSetupForm.step1.linkText')}
      </ExternalLink>
    </StepContainer>
  );
};

const AccessProfileStep = () => {
  const { t } = useTranslation('getting-started');
  return (
    <StepContainer step={2}>
      <Text variant="label-large" mb="1rem">
        {t('abmSetupForm.step2.title')}
      </Text>
      <Text>{t('abmSetupForm.step2.description')}</Text>
    </StepContainer>
  );
};

type ThirdPartyStepProps = {
  onCheck: () => void;
  error: boolean;
  isCheckboxSelected?: boolean;
};

const ThirdPartyStep = ({
  onCheck,
  error,
  isCheckboxSelected,
}: ThirdPartyStepProps) => {
  const { t } = useTranslation('getting-started');
  const { md } = useBreakpoint();
  const { abmCanadaResellerId } = useFlags();
  const electricID = US_ABM_RESELLER_ID;
  const requiredColor = useThemeColors(ColorsV2.ERROR);

  return (
    <StepContainer step={3}>
      <Text variant="label-large" mb="1rem">
        {t('abmSetupForm.step3.title')}
      </Text>
      <Flex vertical rowGap="1rem">
        <Text>{t('abmSetupForm.step3.intro')}</Text>
        <Text> {t('abmSetupForm.step3.stepA')}</Text>
        <Text> {t('abmSetupForm.step3.stepB')}</Text>
        <Text> {t('abmSetupForm.step3.stepC')}</Text>
        {abmCanadaResellerId ? (
          <ResellerIdsList
            resellers={[
              {
                id: US_ABM_RESELLER_ID,
                label: t('abmSetupForm.step3.usResellerLabel'),
              },
              {
                id: CA_ABM_RESELLER_ID,
                label: t('abmSetupForm.step3.caResellerLabel'),
              },
            ]}
          />
        ) : (
          <Flex vertical={!md} alignItems={md ? 'center' : 'flex-start'}>
            <Text mr="2rem">
              {' '}
              {t('abmSetupForm.step3.stepD')} <strong>ID: {electricID}</strong>
            </Text>
            <CopyButton
              id="abm-copy-id-button"
              variant="outline"
              textToCopy={electricID}>
              {t('abmSetupForm.step3.copyButton')}
            </CopyButton>
          </Flex>
        )}
        <Text> {t('abmSetupForm.step3.stepE')}</Text>
        <Text> {t('abmSetupForm.step3.stepF')}</Text>
      </Flex>
      <Flex columnGap="0.5rem" m="1rem 0rem">
        <Text color={requiredColor}>*</Text>
        <Text variant="label-large">
          {t('abmSetupForm.step3.confirmTitle')}
        </Text>
      </Flex>
      <Checkbox
        id="needs-third-party-reseller"
        onChange={onCheck}
        disabled={isCheckboxSelected}
        checked={isCheckboxSelected}>
        {t(
          abmCanadaResellerId
            ? 'abmSetupForm.step3.confirmCheckboxMultiId'
            : 'abmSetupForm.step3.confirmCheckbox'
        )}
      </Checkbox>
      {error && (
        <FieldHelperText
          error
          errorMessage={t('abmSetupForm.step3.confirmError')}
        />
      )}
    </StepContainer>
  );
};

type ProvideAbmOrgIdStepProps = {
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error: boolean;
  errorMessage: string;
  abmID?: number;
};

const ProvideAbmOrgIdStep = ({
  onInputChange,
  error,
  errorMessage,
  abmID,
}: ProvideAbmOrgIdStepProps) => {
  const { t } = useTranslation('getting-started');
  const requiredColor = useThemeColors(ColorsV2.ERROR);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input when the user edits the abm ID
  useEffect(() => {
    if (abmID) {
      inputRef?.current?.focus();
    }
  }, [abmID]);

  return (
    <StepContainer step={4}>
      <Text variant="label-large" mb="1rem">
        {t('abmSetupForm.step4.title')}{' '}
      </Text>
      <Text>{t('abmSetupForm.step4.description')}</Text>
      <Flex columnGap="0.5rem" m="1rem 0rem">
        <Text color={requiredColor}>*</Text>
        <Text variant="label-large">{t('abmSetupForm.step4.label')}</Text>
      </Flex>
      <TextInput
        id="abm-id-input"
        testId="input-valid-abm-id"
        placeholder={t('abmSetupForm.step4.placeholder')}
        onChange={onInputChange}
        defaultValue={abmID?.toString()}
        ref={inputRef}
      />
      {error && <FieldHelperText error errorMessage={errorMessage} />}
    </StepContainer>
  );
};

type ABMSetupFormProps = {
  onSaveAbm: () => void;
  onCancelAbm?: () => void;
  onErrorAbm: () => void;
  abmID?: number;
  isCheckboxSelected?: boolean;
};

type ABMSetupComponentProps = {
  onCancelAbm?: () => void;
  hasFormActionsButtons?: boolean;
  handleCheckbox: () => void;
  handleInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSave: () => void;
  needsValidAbmID: boolean;
  needsThirdParty: boolean;
  abmIDErrorMessage: string;
  abmID?: number;
  isCheckboxSelected?: boolean;
  needsEditAbmID?: boolean;
  isLoading?: boolean;
};

export const ABMSetupComponent = ({
  onCancelAbm,
  hasFormActionsButtons = true,
  handleCheckbox,
  handleInput,
  handleSave,
  needsValidAbmID,
  needsThirdParty,
  abmIDErrorMessage,
  abmID,
  isCheckboxSelected,
  needsEditAbmID,
  isLoading = false,
}: ABMSetupComponentProps) => {
  const { t } = useTranslation('getting-started');
  return (
    <>
      {/* Step 1 */}
      <LoginIntoAbmStep />

      {/* Step 2 */}
      <AccessProfileStep />

      {/* Step 3 */}
      <ThirdPartyStep
        onCheck={handleCheckbox}
        error={needsThirdParty}
        isCheckboxSelected={isCheckboxSelected}
      />

      {/* Step 4 */}
      <ProvideAbmOrgIdStep
        onInputChange={handleInput}
        error={needsValidAbmID}
        errorMessage={abmIDErrorMessage}
        abmID={abmID}
      />

      {/* form actions buttons */}
      {hasFormActionsButtons && (
        <Flex p="2rem" width="100%" justifyContent="flex-end">
          <Flex columnGap="1rem">
            <Button
              id="cancel-abm-setup-button"
              variant="text"
              onClick={onCancelAbm}
              testId="cancel-abm-setup-button">
              {t('abmSetupForm.buttons.cancel')}
            </Button>
            <Button
              id="submit-abm-setup-button"
              onClick={handleSave}
              testId="submit-abm-setup-button"
              disabled={needsThirdParty || needsValidAbmID || needsEditAbmID}
              loading={isLoading}>
              {t('abmSetupForm.buttons.save')}
            </Button>
          </Flex>
        </Flex>
      )}
    </>
  );
};

const ABMSetupForm = ({
  onCancelAbm,
  onSaveAbm,
  onErrorAbm,
  abmID,
  isCheckboxSelected,
}: ABMSetupFormProps) => {
  const { t } = useTranslation('getting-started');
  const {
    handleCheckbox,
    handleInput,
    handleSave,
    needsValidAbmID,
    needsThirdParty,
    abmIDErrorMessage,
    needsEditAbmID,
    isLoading,
  } = useABMSetup({
    onSaveAbm,
    onErrorAbm,
    abmOrgID: abmID?.toString(),
    isCheckboxSelected,
    t,
  });

  return (
    <ABMManagementCard>
      <ABMSetupComponent
        onCancelAbm={onCancelAbm}
        handleCheckbox={handleCheckbox}
        handleInput={handleInput}
        handleSave={handleSave}
        needsValidAbmID={needsValidAbmID}
        needsThirdParty={needsThirdParty}
        abmIDErrorMessage={abmIDErrorMessage}
        abmID={abmID}
        isCheckboxSelected={isCheckboxSelected}
        needsEditAbmID={needsEditAbmID}
        isLoading={isLoading}
      />
    </ABMManagementCard>
  );
};

export default ABMSetupForm;
